import * as React from "react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  HelpCircle,
  Map,
  Settings2,
  Loader2,
  BarChart3,
  ExternalLink,
  KeyRound,
} from "lucide-react"
import toast from "react-hot-toast"

import { NavMain, type Item } from "@/components/nav-main"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { OrganizationSwitcher } from "./organization-switcher"
import BillingInfoCard from "./features/billing/BillingInfoCard"
import { useHelpCenterStore } from "@/store/helpCenterStore"
import { useUserHasHelpCenterEnabled, useIsAdmin } from "@/store/userStore"
import { usePenpal } from "@/hooks/usePenpal"
import { generateBrowserLoginToken } from "@/services/api"

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation();
  const { helpCenters, isLoading, fetchHelpCenters } = useHelpCenterStore();
  const { openExternalUrl, isInIframe: inIframe } = usePenpal();
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const helpCenterEnabled = useUserHasHelpCenterEnabled();
  const isAdmin = useIsAdmin();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  // Fetch help centers on mount
  useEffect(() => {
    if(helpCenterEnabled) {
      fetchHelpCenters();
    }
  }, [fetchHelpCenters, helpCenterEnabled]);


  const handleOpenInBrowser = async () => {
    setIsGeneratingToken(true);
    try {
      const response = await generateBrowserLoginToken();
      if (response.ok && response.url) {
        toast.success(t('sidebar.openingInBrowser'), {
          position: "top-center",
          duration: 3000,
        });
        openExternalUrl(response.url);
      } else {
        throw new Error(response.error || "Failed to generate login token");
      }
    } catch (error) {
      console.error("Error generating browser login token:", error);
      toast.error(
        t('sidebar.openInBrowserError'),
        {
          position: "top-center",
          duration: 5000,
        }
      );
    } finally {
      setIsGeneratingToken(false);
    }
  };

  // Build navigation items with help centers
  const navigationTop = useMemo(() => {
    let helpCenterChildren: Item[] = [];

    // Add loading indicator when fetching
    if (isLoading) {
      helpCenterChildren.push({
        title: t('common.loading'),
        tooltip: t('common.loading'),
        icon: Loader2,
      } as Item);
    } else if (helpCenters.length > 0) {
      // Add help centers as navigation items
      helpCenters.forEach((helpCenter) => {
        helpCenterChildren.push({
          title: helpCenter.title,
          url: `/help-centers/${helpCenter.id}/structure`,
          tooltip: helpCenter.description || helpCenter.title,
        });
      });
    }
    if(!helpCenterEnabled) {
      helpCenterChildren = [];
    }
    return [
      isAdmin ? {
        title: t('sidebar.licenseManager'),
        url: "/license-manager",
        icon: KeyRound,
        tooltip: t('sidebar.licenseManagerTooltip'),
        highlight: true,
      } : null,
      {
        title: t('sidebar.guides'),
        url: "/guides",
        tooltip: t('sidebar.guidesTooltip'),
        icon: Map,
      },
      helpCenterEnabled ? {
        title: t('sidebar.helpCenters'),
        icon: HelpCircle,
        url: "/help-centers",
        children: helpCenterChildren,
        badge: t('sidebar.badgeNew'),
      } : null,
      {
        title: t('sidebar.guidesBranding'),
        url: "/settings/shared-guides",
        icon: Settings2,
        tooltip: t('sidebar.guidesBrandingTooltip'),
      },
      helpCenterEnabled ? {
        title: t('sidebar.reports'),
        url: "/reports",
        icon: BarChart3,
        tooltip: t('sidebar.reportsTooltip'),
      } : null,
    ].filter(Boolean) as Item[];
  }, [helpCenters, isLoading, isAdmin, t]);

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <OrganizationSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navigationTop} className="mt-2"/>
        <div className="mx-2 mt-auto mb-4">
          <BillingInfoCard hideHelpCenters={inIframe} />
        </div>
      </SidebarContent>
      {inIframe && helpCenterEnabled && (
        <SidebarFooter className="border-t border-sidebar-border pt-2">
          <div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full h-8 font-medium hover:bg-sidebar-accent hover:text-sidebar-accent-foreground border-primary shadow-none border-2 justify-center`}
                    onClick={handleOpenInBrowser}
                    disabled={isGeneratingToken}
                  >
                    {isGeneratingToken ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {!isCollapsed && <span>{t('nav.opening')}</span>}
                      </>
                    ) : (
                      <>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        {!isCollapsed && <span className="ml-2">{t('sidebar.openInBrowser')}</span>}
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>{t('sidebar.openInBrowserTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </SidebarFooter>
      )}
      <SidebarRail />
    </Sidebar>
  )
}
